/**
 * FN-FE-03 + FN-FIX-B1 — Step 2: Cégadatok (kötelező)
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import { post } from '../../lib/api';
import AddressForm, { emptyAddress } from '../../components/AddressForm';
import type { AddressData } from '../../components/AddressForm';
import { useToast } from '../../components/ui/Toast';

function StepDots({ current, total }: { current: number; total: number }) {
  return (
    <div className="onb-steps">
      {Array.from({ length: total }, (_, i) => (
        <div key={i} className={`onb-dot${i + 1 < current ? ' done' : i + 1 === current ? ' active' : ''}`} />
      ))}
    </div>
  );
}

export { StepDots };

export default function OnboardingCompanyStep() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(() => {
    // NAV lookup adatok előtöltése (cégváltás / új cég létrehozás után)
    try {
      const raw = sessionStorage.getItem('nav_prefill');
      if (raw) {
        sessionStorage.removeItem('nav_prefill');
        const d = JSON.parse(raw);
        const addr = (d.addresses && d.addresses[0]) || d;
        return {
          name: d.name || d.taxpayerName || '',
          taxNumber: d.taxNumber || '',
          address: {
            postalCode: addr.postalCode || '',
            city: addr.city || '',
            streetName: addr.streetName || '',
            streetType: (addr.streetType || addr.publicPlaceCategory || '').toLowerCase(),
            houseNumber: addr.houseNumber || addr.number || '',
            building: addr.building || '',
            staircase: addr.staircase || '',
            floor: addr.floor || '',
            door: addr.door || '',
          } as AddressData,
          bankAccount: '', email: '', phone: '',
        };
      }
    } catch { /* silent */ }
    return {
      name: '', taxNumber: '',
      address: { ...emptyAddress } as AddressData,
      bankAccount: '', email: '', phone: '',
    };
  });

  const set = (key: string) => (e: React.ChangeEvent<HTMLInputElement>) => setForm(f => ({ ...f, [key]: e.target.value }));

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      await post('/setup/company', {
        name: form.name,
        taxNumber: form.taxNumber,
        postalCode: form.address.postalCode,
        city: form.address.city,
        streetName: form.address.streetName,
        streetType: form.address.streetType,
        houseNumber: form.address.houseNumber,
        building: form.address.building,
        staircase: form.address.staircase,
        floor: form.address.floor,
        door: form.address.door,
        bankAccount: form.bankAccount,
        email: form.email,
        phone: form.phone,
      });
      navigate('/onboarding/nav');
    } catch (err: any) { toast(err.message || 'Hiba történt.', 'error'); } finally { setLoading(false); }
  }

  const isValid = form.name.length >= 2 && form.taxNumber.length >= 11 && form.address.postalCode.length === 4 && form.address.city.length >= 2;

  return (
    <div className="onb-bg">
      <div className="onb-wrap">
        <StepDots current={2} total={5} />

        <h1 className="onb-title">Cégadatok</h1>
        <p className="onb-sub">Töltsd ki a céged adatait. Ezek jelennek meg a számláidon mint kibocsátói adatok.</p>

        <div className="onb-card">
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <Input label="Cégnév *" value={form.name} onChange={set('name')} placeholder="Példa Kft." />
              <Input label="Adószám *" value={form.taxNumber} onChange={set('taxNumber')} placeholder="12345678-2-43" helperText="Formátum: 8 jegy - 1 jegy - 2 jegy" />

              <AddressForm
                value={form.address}
                onChange={(addr) => setForm(f => ({ ...f, address: addr }))}
                required
              />

              <Input label="Bankszámlaszám *" value={form.bankAccount} onChange={set('bankAccount')} placeholder="12345678-12345678-12345678" helperText="Hazai 2×8 vagy 3×8 formátum." />
              <Input label="Kapcsolattartási e-mail" type="email" value={form.email} onChange={set('email')} placeholder="info@ceged.hu" />
              <Input label="Telefonszám" value={form.phone} onChange={set('phone')} placeholder="+36 1 234 5678" />
            </div>

            <div className="onb-footer">
              <Button type="submit" disabled={!isValid} loading={loading}>Tovább a NAV-kapcsolathoz →</Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
